import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import { useAppDispatch } from '../../../../store/hooks'
import { setCrossClientHasActions } from '../../../../store/crossClient/crossClientResults'
import './CrossClientResultsFilter.scss'

interface ActionsFilterOption {
  key: string
  label: string
  value: boolean | null
}

const OPTIONS: ActionsFilterOption[] = [
  { key: 'all', label: 'All actions', value: null },
  { key: 'with', label: 'With actions', value: true },
  { key: 'without', label: 'Without actions', value: false },
]

const optionFromParam = (param: string | null): ActionsFilterOption => {
  if (param === 'true') {
    return OPTIONS[1]
  }
  if (param === 'false') {
    return OPTIONS[2]
  }
  return OPTIONS[0]
}

interface ActionsFilterToggleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string
}

const ActionsFilterToggle = React.forwardRef<HTMLButtonElement, ActionsFilterToggleProps>(({ label, className, ...rest }, ref) => (
  <button
    ref={ref}
    type='button'
    aria-label='Filter by actions'
    className={classNames(
      'd-flex gap-2 align-items-center form-control rounded-3 zocks-px-0.75 zocks-py-0.375 text-start cross-client-results-filter',
      className,
    )}
    {...rest}
  >
    <i className='bi bi-filter fs-6' />
    <span className='flex-grow-1 text-nowrap text-truncate paragraph-small'>{label}</span>
    <i className='bi bi-chevron-expand fs-6' />
  </button>
))
ActionsFilterToggle.displayName = 'ActionsFilterToggle'

export const CrossClientActionsFilter = () => {
  const dispatch = useAppDispatch()

  const [searchParams, setSearchParams] = useSearchParams()

  const initialOption = optionFromParam(searchParams.get('hasActions'))
  const [selectedKey, setSelectedKey] = useState<string>(initialOption.key)

  const initialized = useRef(false)

  const applyOption = useCallback(
    (option: ActionsFilterOption) => {
      setSelectedKey(option.key)
      dispatch(setCrossClientHasActions(option.value))

      setSearchParams(
        (prev) => {
          if (option.value === null) {
            prev.delete('hasActions')
          } else {
            prev.set('hasActions', String(option.value))
          }
          return prev
        },
        { replace: true },
      )
    },
    [dispatch, setSearchParams],
  )

  useEffect(() => {
    if (initialized.current) {
      return
    }
    initialized.current = true
    if (initialOption.value !== null) {
      dispatch(setCrossClientHasActions(initialOption.value))
    }
  }, [dispatch, initialOption.value])

  const selected = OPTIONS.find((option) => option.key === selectedKey) ?? OPTIONS[0]

  return (
    <Dropdown align='end'>
      <Dropdown.Toggle as={ActionsFilterToggle} bsPrefix='cross-client-actions-filter' label={selected.label} />
      <Dropdown.Menu className='cross-client-actions-filter-menu p-1'>
        {OPTIONS.map((option) => (
          <Dropdown.Item
            key={option.key}
            active={option.key === selected.key}
            onClick={() => applyOption(option)}
            className='paragraph-small rounded-2 px-2 py-1'
          >
            {option.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  )
}
